import React, { useState, useEffect } from 'react';
import { Upload, Check, CreditCard, QrCode, Loader, ArrowLeft, ArrowRight, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

const InscripcionesEstudiante = () => {
  const [step, setStep] = useState(1); // Paso actual: 1 o 2
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [carreras, setCarreras] = useState([]);
  const [menciones, setMenciones] = useState([]);

  // Validación de periodo de matriculación
  const [periodoHabilitado, setPeriodoHabilitado] = useState(false); // Por defecto cerrado hasta verificar
  const [periodoInfo, setPeriodoInfo] = useState(null);

  // Datos generales
  const [datosGenerales, setDatosGenerales] = useState({
    id_carrera: '',
    id_mencion: '',
    modalidad_postulacion: '',
    id_gestion: new Date().getFullYear()
  });

  // Datos personales completos
  const [datosPersonales, setDatosPersonales] = useState({
    nombres: '',
    apellido_paterno: '',
    apellido_materno: '',
    cedula_identidad: '',
    ci_extension: '',
    ci_complemento: '',
    fecha_nacimiento: '',
    edad: '',
    lugar_nacimiento: '',
    direccion: '',
    telefono: '',
    correo_electronico: '',
    familiar_nombres: '',
    familiar_parentesco: '',
    familiar_telefono: '',
    contacto_nombres: '',
    contacto_parentesco: '',
    contacto_telefono: '',
    tipo_sangre: '',
    alergias: '',
    enfermedad_base: '',
    lesiones_otros: '',
    sexo: ''
  });

  // Campos que vienen de preadmisión y no deben editarse
  const [camposPreadmision, setCamposPreadmision] = useState({
    nombres: false,
    apellido_paterno: false,
    apellido_materno: false,
    cedula_identidad: false,
    ci_extension: false,
    fecha_nacimiento: false
  });

  const [tipoPago, setTipoPago] = useState('qr');

  // Archivos de documentación
  const [files, setFiles] = useState({
    titulo_bachiller: null,
    cedula_identidad: null,
    comprobante_deposito: null,
    comprobante_tramites: null,
    compromiso: null,
    seguro_medico: null,
    multa_retraso: null,
    foto_carnet: null
  });

  const [declaracionAceptada, setDeclaracionAceptada] = useState(false);
  const [inscripcionEnviada, setInscripcionEnviada] = useState(false);
  const [error, setError] = useState('');
  const [existingDocs, setExistingDocs] = useState({});


  // Cargar datos iniciales
  useEffect(() => {
    const loadData = async () => {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const token = localStorage.getItem('token');
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

        // Verificar periodo de matriculación (ID 3: Inscripción a matriculación)
        try {
          // const periodoRes = await fetch(`${API_URL}/direccion/parametrizaciones/verificar/${encodeURIComponent('Inscripción a matriculación')}`);
          const periodoRes = await fetch(`${API_URL}/direccion/parametrizaciones/verificar/${encodeURIComponent('Proceso de matriculación')}`);
          if (periodoRes.ok) {
            const periodoData = await periodoRes.json();
            setPeriodoHabilitado(periodoData.habilitado);
            setPeriodoInfo(periodoData);
          }
        } catch (e) {
          console.log('Verificación de periodo no disponible');
        }

        // Cargar datos del estudiante desde admisión (inscripciones_preadmision o estudiantes_kardex)
        try {
          const infoRes = await fetch(`${API_URL}/estudiante/info-matriculacion`, { headers });
          if (infoRes.ok) {
            const infoData = await infoRes.json();
            if (infoData.estudiante) {
              const est = infoData.estudiante;

              // Marcar campos que vienen de preadmisión como readonly
              setCamposPreadmision({
                nombres: !!est.nombres,
                apellido_paterno: !!(est.apellido_paterno || est.apellidos),
                apellido_materno: !!(est.apellido_materno || est.apellidos?.split(' ')[1]),
                cedula_identidad: !!est.cedula_identidad,
                ci_extension: !!(est.ci_extension || est.cedula_identidad?.includes('-')),
                fecha_nacimiento: !!est.fecha_nacimiento
              });

              // Helper para formatear fecha a YYYY-MM-DD para input date
              const formatDateForInput = (dateString) => {
                if (!dateString) return '';
                try {
                  const d = new Date(dateString);
                  if (isNaN(d.getTime())) return '';
                  return d.toISOString().split('T')[0];
                } catch (e) { return ''; }
              };

              setDatosPersonales(prev => ({
                ...prev,
                nombres: est.nombres || '',
                apellido_paterno: est.apellido_paterno || est.apellidos?.split(' ')[0] || '',
                apellido_materno: est.apellido_materno || est.apellidos?.split(' ')[1] || '',
                cedula_identidad: est.cedula_identidad?.split('-')[0] || '',
                ci_extension: est.ci_extension || est.cedula_identidad?.split('-')[1] || '',
                fecha_nacimiento: formatDateForInput(est.fecha_nacimiento),
                edad: est.edad || '',
                lugar_nacimiento: est.lugar_nacimiento || '',
                direccion: est.direccion || '',
                telefono: est.telefono || '',
                correo_electronico: est.email || user.email || '',
                familiar_nombres: est.familiar_nombres || '',
                familiar_parentesco: est.familiar_parentesco || '',
                familiar_telefono: est.familiar_telefono || '',
                contacto_nombres: est.contacto_nombres || '',
                contacto_parentesco: est.contacto_parentesco || '',
                contacto_telefono: est.contacto_telefono || '',
                tipo_sangre: est.tipo_sangre || '',
                alergias: est.alergias || '',
                enfermedad_base: est.enfermedad_base || '',
                lesiones_otros: est.lesiones_otros || '',
                sexo: est.sexo || ''
              }));
              setDatosGenerales(prev => ({
                ...prev,
                id_carrera: est.id_carrera || '',
                id_mencion: est.id_mencion || '',
                modalidad_postulacion: (() => {
                  const m = (est.modalidad_postulacion || '').toUpperCase();
                  if (m.includes('CPA') || m.includes('CURSO')) return 'Curso de Preadmisión';
                  if (m.includes('PAA') || m.includes('PRUEBA')) return 'Prueba de Admisión Académica';
                  if (m.includes('ESPECIAL')) return 'Admisión Especial';
                  return est.modalidad_postulacion || '';
                })()
              }));
              // Documentos existentes de preadmisión o kardex
              setExistingDocs({
                titulo_bachiller: est.doc_titulo || est.titulo_bachiller_o_certificado || null,
                cedula_identidad: est.doc_cedula || est.foto_ci || null,
                comprobante_deposito: est.doc_comprobante || est.comprobante_pago || null,
                compromiso: est.doc_compromiso || null,
                seguro_medico: est.doc_seguro || null,
                foto_carnet: est.doc_foto || est.foto_postulante || null
              });
            }
          }
        } catch (e) {
          console.log('Info matriculación no disponible, usando fallback');
          setDatosPersonales(prev => ({
            ...prev,
            nombres: user.nombre || '',
            apellido_paterno: user.apellido || '',
            correo_electronico: user.email || ''
          }));
        }

        // Configuración de pago (Matricualción usa preadmision por ahora)
        const paymentRes = await fetch(`${API_URL}/auth/payment-config/preadmision`);
        if (paymentRes.ok) {
          const paymentData = await paymentRes.json();
          setPaymentConfig(paymentData);

          // Seleccionar automáticamente el primer método disponible
          if (paymentData.qr_activo === true) {
            setTipoPago('qr');
          } else if (paymentData.banco_activo === true) {
            setTipoPago('transferencia');
          }
        }

        // Carreras
        const carrerasRes = await fetch(`${API_URL}/jefatura/carreras`);
        if (carrerasRes.ok) {
          const carrerasData = await carrerasRes.json();
          setCarreras(carrerasData.carreras || []);
        }

        // Verificar si ya existe matriculación
        try {
          const matriculaRes = await fetch(`${API_URL}/estudiante/verificar-matriculacion`, { headers });
          if (matriculaRes.ok) {
            const matriculaData = await matriculaRes.json();
            if (matriculaData.yaMatriculado) {
              setInscripcionEnviada(true);
            }
          }
        } catch (e) {
          console.log('Verificación de matriculación no disponible');
        }

      } catch (error) {
        console.error('Error cargando datos:', error);
        setError('Error al cargar datos');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Cargar menciones cuando cambia la carrera
  useEffect(() => {
    const loadMenciones = async () => {
      if (datosGenerales.id_carrera) {
        try {
          const res = await fetch(`${API_URL}/jefatura/menciones?id_carrera=${datosGenerales.id_carrera}`);
          if (res.ok) {
            const data = await res.json();
            setMenciones(data.menciones || []);
          }
        } catch (e) {
          setMenciones([]);
        }
      } else {
        setMenciones([]);
      }
    };
    loadMenciones();
  }, [datosGenerales.id_carrera]);

  // Calcular edad automáticamente
  useEffect(() => {
    if (datosPersonales.fecha_nacimiento) {
      const birth = new Date(datosPersonales.fecha_nacimiento);
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--;
      }
      setDatosPersonales(prev => ({ ...prev, edad: age.toString() }));
    }
  }, [datosPersonales.fecha_nacimiento]);
  
  // Polling: detectar matrícula procesada por admin sin recargar la página
useEffect(() => {
  if (inscripcionEnviada || loading) return; // ya está matriculado o aún cargando

  const token = localStorage.getItem('token');
  const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

  const verificar = async () => {
    try {
      const res = await fetch(`${API_URL}/estudiante/verificar-matriculacion`, { headers });
      if (res.ok) {
        const data = await res.json();
        if (data.yaMatriculado) {
          setInscripcionEnviada(true); // cambia la vista automáticamente
        }
      }
    } catch (e) { /* silencioso */ }
  };

  const intervalo = setInterval(verificar, 10000); // cada 10 segundos
  return () => clearInterval(intervalo);            // limpia al desmontar
}, [inscripcionEnviada, loading]);

  const handleDatosGeneralesChange = (e) => {
    const { name, value } = e.target;
    setDatosGenerales(prev => ({ ...prev, [name]: value }));
  };

  const handleDatosPersonalesChange = (e) => {
    const { name, value } = e.target;
    setDatosPersonales(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (fieldName, e) => {
    const file = e.target.files[0];
    if (file) {
      setFiles(prev => ({ ...prev, [fieldName]: file }));
    }
  };

  const validateStep1 = () => {
    const required = {
      id_carrera: datosGenerales.id_carrera,
      nombres: datosPersonales.nombres,
      apellido_paterno: datosPersonales.apellido_paterno,
      cedula_identidad: datosPersonales.cedula_identidad,
      fecha_nacimiento: datosPersonales.fecha_nacimiento,
      lugar_nacimiento: datosPersonales.lugar_nacimiento,
      direccion: datosPersonales.direccion,
      telefono: datosPersonales.telefono,
      correo_electronico: datosPersonales.correo_electronico,
      familiar_nombres: datosPersonales.familiar_nombres,
      familiar_parentesco: datosPersonales.familiar_parentesco,
      familiar_telefono: datosPersonales.familiar_telefono,
      contacto_nombres: datosPersonales.contacto_nombres,
      contacto_parentesco: datosPersonales.contacto_parentesco,
      contacto_telefono: datosPersonales.contacto_telefono,
      tipo_sangre: datosPersonales.tipo_sangre,
      alergias: datosPersonales.alergias,
      enfermedad_base: datosPersonales.enfermedad_base,
      lesiones_otros: datosPersonales.lesiones_otros,
      sexo: datosPersonales.sexo
    };

    const empty = Object.entries(required)
      .filter(([_, val]) => !val || val.toString().trim() === '')
      .map(([key]) => key);

    if (empty.length > 0) {
      const names = {
        id_carrera: 'Carrera',
        nombres: 'Nombres',
        apellido_paterno: 'Apellido Paterno',
        cedula_identidad: 'Cédula de Identidad',
        fecha_nacimiento: 'Fecha de Nacimiento',
        lugar_nacimiento: 'Lugar de Nacimiento',
        direccion: 'Dirección',
        telefono: 'Teléfono',
        correo_electronico: 'Correo Electrónico',
        familiar_nombres: 'Familiar Cercano',
        familiar_parentesco: 'Parentesco Familiar',
        familiar_telefono: 'Teléfono Familiar',
        contacto_nombres: 'Contacto de Emergencia',
        contacto_parentesco: 'Parentesco Contacto',
        contacto_telefono: 'Teléfono Contacto',
        tipo_sangre: 'Tipo de Sangre',
        alergias: 'Alergias',
        enfermedad_base: 'Enfermedad de Base',
        lesiones_otros: 'Lesiones u Otros',
        sexo: 'Género'
      };
      
      const missing = empty.map(c => names[c] || c).join(', ');
      setError(`Por favor complete los campos obligatorios: ${missing}`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return false;
    }
    setError('');
    return true;
  };

  const handleNextStep = () => {
    if (validateStep1()) {
      setStep(2);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 1. Validar Paso 1 nuevamente por seguridad
    if (!validateStep1()) return;

    // 2. Validar Paso 2 (Documentación y Pago)
    if (!declaracionAceptada) {
      setError('Debe aceptar la declaración jurada para continuar');
      return;
    }

    // Archivos obligatorios mínimos (si no existen ya de preadmisión)
    const requiredFiles = [];
    if (!existingDocs.titulo_bachiller && !files.titulo_bachiller) requiredFiles.push('Fotografía Título de Bachiller');
    if (!existingDocs.cedula_identidad && !files.cedula_identidad) requiredFiles.push('Fotografía Cédula de Identidad');
    if (!existingDocs.comprobante_deposito && !files.comprobante_deposito) requiredFiles.push('Comprobante de Depósito/Pago');
    if (!existingDocs.compromiso && !files.compromiso) requiredFiles.push('Compromiso');
    if (!existingDocs.seguro_medico && !files.seguro_medico) requiredFiles.push('Fotografía Constancia Seguro Médico');
    
    if (requiredFiles.length > 0) {
      setError(`Faltan documentos obligatorios: ${requiredFiles.join(', ')}`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const formDataToSend = new FormData();

      // Datos generales
      formDataToSend.append('id_carrera', datosGenerales.id_carrera);
      formDataToSend.append('id_mencion', datosGenerales.id_mencion);
      formDataToSend.append('modalidad_postulacion', datosGenerales.modalidad_postulacion);

      // Datos personales
      Object.entries(datosPersonales).forEach(([key, value]) => {
        formDataToSend.append(key, value || '');
      });

      formDataToSend.append('tipo_pago', tipoPago);
      formDataToSend.append('declaracion_aceptada', declaracionAceptada);

      // Archivos
      Object.entries(files).forEach(([key, file]) => {
        if (file) {
          formDataToSend.append(key, file);
        }
      });

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/estudiante/inscripcion-matriculacion`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formDataToSend
      });

      const data = await response.json();

      if (response.ok) {
        setInscripcionEnviada(true);
      } else {
        setError(data.message || 'Error al enviar inscripción');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      console.error('Error:', error);
      setError('Error de conexión al enviar inscripción. Revise su conexión a internet.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center items-center">
        <Loader className="animate-spin mr-2" /> Cargando información...
      </div>
    );
  }

  if (inscripcionEnviada) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
          <Check size={64} className="mx-auto text-green-500 mb-4" />
          <h2 className="text-2xl font-bold text-green-800 mb-4">¡Matriculación Exitosa!</h2>
          <p className="text-gray-700">A fin de completar su proceso de inscripción, debe apersonarse al área de Kardex para realizar el recojo de su Hoja de Registro Personal y Matrícula.</p>
        </div>
      </div>
    );
  }

  const tiposSangre = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

  // Si el periodo está cerrado, mostrar mensaje
  if (!periodoHabilitado && !inscripcionEnviada) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <div className="mb-6">
          <h1 className="text-xl font-bold text-gray-800">Estudiante &gt; Matriculación</h1>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
          <AlertTriangle size={64} className="mx-auto text-yellow-500 mb-4" />
          <h2 className="text-2xl font-bold text-yellow-800 mb-4">Periodo de Matriculación Cerrado</h2>
          <p className="text-gray-700">
            El periodo de matriculación no está habilitado actualmente.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-800">Estudiante &gt; Matriculación</h1>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* ========== PASO 1 ========== */}
        {step === 1 && (
          <div className="space-y-6">
            {/* 1. Datos Generales */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4 text-blue-900">1. Datos Generales</h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Carrera*</label>
                  <select name="id_carrera" value={datosGenerales.id_carrera} onChange={handleDatosGeneralesChange}
                    className="w-full px-3 py-2 border rounded-md" required>
                    <option value=""></option>
                    {carreras.map(c => (
                      <option key={c.id_carrera} value={c.id_carrera}>{c.nombre}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Mención</label>
                  <select name="id_mencion" value={datosGenerales.id_mencion} onChange={handleDatosGeneralesChange}
                    className="w-full px-3 py-2 border rounded-md">
                    <option value=""></option>
                    {menciones.map(m => (
                      <option key={m.id_mencion} value={m.id_mencion}>{m.nombre}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Modalidad de Admisión</label>
                  <input type="text" name="modalidad_postulacion" value={datosGenerales.modalidad_postulacion}
                    readOnly
                    className="w-full px-3 py-2 border rounded-md bg-gray-100 cursor-not-allowed"
                    placeholder="Se obtiene automáticamente de su admisión" />
                </div>
              </div>
            </div>

            {/* 2. Datos Personales */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4 text-blue-900">2. Datos Personales</h2>

              {/* Fila 1: Nombres */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Nombres*</label>
                  <input type="text" name="nombres" value={datosPersonales.nombres}
                    onChange={camposPreadmision.nombres ? undefined : handleDatosPersonalesChange}
                    readOnly={camposPreadmision.nombres}
                    className={`w-full px-3 py-2 border rounded-md ${camposPreadmision.nombres ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    placeholder="Ej: Juan Carlos" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Apellido Paterno*</label>
                  <input type="text" name="apellido_paterno" value={datosPersonales.apellido_paterno}
                    onChange={camposPreadmision.apellido_paterno ? undefined : handleDatosPersonalesChange}
                    readOnly={camposPreadmision.apellido_paterno}
                    className={`w-full px-3 py-2 border rounded-md ${camposPreadmision.apellido_paterno ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    placeholder="Ej: Pérez" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Apellido Materno</label>
                  <input type="text" name="apellido_materno" value={datosPersonales.apellido_materno}
                    onChange={camposPreadmision.apellido_materno ? undefined : handleDatosPersonalesChange}
                    readOnly={camposPreadmision.apellido_materno}
                    className={`w-full px-3 py-2 border rounded-md ${camposPreadmision.apellido_materno ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    placeholder="Ej: López" />
                </div>
              </div>

              {/* Fila 2: CI y Fecha */}
              <div className="grid grid-cols-2 md:grid-cols-7 gap-4 mb-4">
                <div className="col-span-1">
                  <label className="block text-sm font-medium mb-1">Cédula de Identidad*</label>
                  <input type="text" name="cedula_identidad" value={datosPersonales.cedula_identidad}
                    onChange={camposPreadmision.cedula_identidad ? undefined : handleDatosPersonalesChange}
                    readOnly={camposPreadmision.cedula_identidad}
                    className={`w-full px-3 py-2 border rounded-md ${camposPreadmision.cedula_identidad ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    placeholder="Ej: 12345678" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Extensión</label>
                  <input type="text" name="ci_extension" value={datosPersonales.ci_extension}
                    onChange={camposPreadmision.ci_extension ? undefined : handleDatosPersonalesChange}
                    readOnly={camposPreadmision.ci_extension}
                    className={`w-full px-3 py-2 border rounded-md ${camposPreadmision.ci_extension ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    placeholder="LP" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Complemento</label>
                  <input type="text" name="ci_complemento" value={datosPersonales.ci_complemento} onChange={handleDatosPersonalesChange}
                    className="w-full px-3 py-2 border rounded-md" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Fecha Nacimiento*</label>
                  <input type="date" name="fecha_nacimiento" value={datosPersonales.fecha_nacimiento}
                    onChange={camposPreadmision.fecha_nacimiento ? undefined : handleDatosPersonalesChange}
                    readOnly={camposPreadmision.fecha_nacimiento}
                    className={`w-full px-3 py-2 border rounded-md ${camposPreadmision.fecha_nacimiento ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Edad*</label>
                  <input type="text" name="edad" value={datosPersonales.edad} readOnly
                    className="w-full px-3 py-2 border rounded-md bg-gray-100" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Lugar Nacimiento*</label>
                  <input type="text" name="lugar_nacimiento" value={datosPersonales.lugar_nacimiento} onChange={handleDatosPersonalesChange}
                    className="w-full px-3 py-2 border rounded-md" placeholder="Ej: La Paz" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Género*</label>
                  <select name="sexo" value={datosPersonales.sexo} onChange={handleDatosPersonalesChange}
                    className="w-full px-3 py-2 border rounded-md" required>
                    <option value=""></option>
                    <option value="M">Masculino (M)</option>
                    <option value="F">Femenino (F)</option>
                  </select>
                </div>
              </div>

              {/* Fila 3: Contacto */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Dirección*</label>
                  <input type="text" name="direccion" value={datosPersonales.direccion} onChange={handleDatosPersonalesChange}
                    className="w-full px-3 py-2 border rounded-md" placeholder="Ej: Av. Arce #1234" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Número de Teléfono/Celular*</label>
                  <input type="text" name="telefono" value={datosPersonales.telefono} onChange={handleDatosPersonalesChange}
                    className="w-full px-3 py-2 border rounded-md" placeholder="Ej: 76543210" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Correo Electrónico*</label>
                  <input type="email" name="correo_electronico" value={datosPersonales.correo_electronico} onChange={handleDatosPersonalesChange}
                    className="w-full px-3 py-2 border rounded-md" placeholder="Ej: correo@ejemplo.com" required />
                </div>
              </div>

              {/* Fila 4: Familiar Cercano */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Nombres y Apellidos Familiar Cercano*</label>
                  <input type="text" name="familiar_nombres" value={datosPersonales.familiar_nombres} onChange={handleDatosPersonalesChange}
                    className="w-full px-3 py-2 border rounded-md" placeholder="Ej: María López Sánchez" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Parentesco*</label>
                  <input type="text" name="familiar_parentesco" value={datosPersonales.familiar_parentesco} onChange={handleDatosPersonalesChange}
                    className="w-full px-3 py-2 border rounded-md" placeholder="Ej: Madre" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Número de Teléfono/Celular*</label>
                  <input type="text" name="familiar_telefono" value={datosPersonales.familiar_telefono} onChange={handleDatosPersonalesChange}
                    className="w-full px-3 py-2 border rounded-md" placeholder="Ej: 76543210" required />
                </div>
              </div>

              {/* Fila 5: Contacto Emergencia */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Nombres y Apellidos Contacto Emergencia*</label>
                  <input type="text" name="contacto_nombres" value={datosPersonales.contacto_nombres} onChange={handleDatosPersonalesChange}
                    className="w-full px-3 py-2 border rounded-md" placeholder="Ej: Pedro Gómez Rojas" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Parentesco*</label>
                  <input type="text" name="contacto_parentesco" value={datosPersonales.contacto_parentesco} onChange={handleDatosPersonalesChange}
                    className="w-full px-3 py-2 border rounded-md" placeholder="Ej: Tío" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Número de Teléfono/Celular*</label>
                  <input type="text" name="contacto_telefono" value={datosPersonales.contacto_telefono} onChange={handleDatosPersonalesChange}
                    className="w-full px-3 py-2 border rounded-md" placeholder="Ej: 71234567" required />
                </div>
              </div>

              {/* Fila 6: Salud */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Tipo de Sangre*</label>
                  <select name="tipo_sangre" value={datosPersonales.tipo_sangre} onChange={handleDatosPersonalesChange}
                    className="w-full px-3 py-2 border rounded-md" required>
                    <option value=""></option>
                    {tiposSangre.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Alergias*</label>
                  <input type="text" name="alergias" value={datosPersonales.alergias} onChange={handleDatosPersonalesChange}
                    className="w-full px-3 py-2 border rounded-md" placeholder="Ninguna" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Enfermedad de Base*</label>
                  <input type="text" name="enfermedad_base" value={datosPersonales.enfermedad_base} onChange={handleDatosPersonalesChange}
                    className="w-full px-3 py-2 border rounded-md" placeholder="Ninguna" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Lesiones u Otros Vinculados a su Salud*</label>
                  <input type="text" name="lesiones_otros" value={datosPersonales.lesiones_otros} onChange={handleDatosPersonalesChange}
                    className="w-full px-3 py-2 border rounded-md" placeholder="Ninguna" required />
                </div>
              </div>
            </div>

            {/* Botón Siguiente */}
            <div className="flex justify-end">
              <button type="button" onClick={handleNextStep}
                className="px-8 py-3 bg-blue-900 text-white rounded-full font-semibold hover:bg-blue-800 transition flex items-center gap-2">
                Siguiente <ArrowRight size={18} />
              </button>
            </div>
          </div>
        )}

        {/* ========== PASO 2 ========== */}
        {step === 2 && (
          <div className="space-y-6">
            {/* 3. Pago de Inscripción */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4 text-blue-900">3. Pago de Inscripción</h2>

              <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-6">
                <p className="text-sm text-amber-800 font-medium">
                  Realiza el pago de inscripción mediante QR o transferencia bancaria
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {/* QR */}
                {paymentConfig?.qr_activo === true && (
                  <div
                    onClick={() => setTipoPago('qr')}
                    className={`relative cursor-pointer rounded-xl border-2 p-6 transition-all hover:shadow-md ${tipoPago === 'qr' ? 'border-blue-500 bg-blue-50/50' : 'border-gray-200 bg-white'}`}
                  >
                    <div className="text-center">
                      <h3 className="font-bold text-gray-800 mb-4">Pago con QR</h3>
                      {paymentConfig?.qr_imagen_url ? (
                        <img src={paymentConfig.qr_imagen_url} alt="QR" className="w-40 h-40 mx-auto border rounded shadow-sm mb-4" />
                      ) : (
                        <div className="w-40 h-40 mx-auto bg-gray-100 rounded flex items-center justify-center mb-4">
                          <QrCode size={48} className="text-gray-400" />
                        </div>
                      )}

                      {tipoPago === 'qr' && (
                        <div className="absolute top-4 right-4 bg-blue-500 text-white rounded-full p-1">
                          <Check size={16} />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Transferencia */}
                {paymentConfig?.banco_activo === true && (
                  <div
                    onClick={() => setTipoPago('transferencia')}
                    className={`relative cursor-pointer rounded-xl border-2 p-6 transition-all hover:shadow-md ${tipoPago === 'transferencia' ? 'border-blue-500 bg-blue-50/50' : 'border-gray-200 bg-white'}`}
                  >
                    <div className="h-full flex flex-col">
                      <h3 className="font-bold text-gray-800 mb-4 text-center">Transferencia Bancaria</h3>

                      <div className="flex-1 flex flex-col justify-center gap-4 text-sm text-gray-700 bg-gray-50 p-4 rounded-lg">
                        <div>
                          <p className="text-xs text-gray-500 uppercase font-semibold">Banco</p>
                          <p className="font-medium">{paymentConfig?.banco_nombre || 'No disponible'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase font-semibold">Número de Cuenta</p>
                          <p className="font-mono font-bold text-lg">{paymentConfig?.banco_cuenta || 'No disponible'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase font-semibold">Titular</p>
                          <p className="font-medium">{paymentConfig?.banco_titular || 'No disponible'}</p>
                        </div>
                      </div>

                      {tipoPago === 'transferencia' && (
                        <div className="absolute top-4 right-4 bg-blue-500 text-white rounded-full p-1">
                          <Check size={16} />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Mensaje si ambos están desactivados */}
                {paymentConfig?.qr_activo === false && paymentConfig?.banco_activo === false && (
                  <div className="col-span-1 md:col-span-2 bg-rose-50 border-2 border-rose-200 rounded-3xl p-12 text-center animate-in zoom-in duration-300">
                    <div className="text-6xl mb-6 text-rose-500">💳❌</div>
                    <h4 className="text-2xl font-black text-rose-900 uppercase tracking-tight mb-4">
                      Pagos Digitales Inhabilitados
                    </h4>
                    <p className="text-rose-700 font-medium max-w-sm mx-auto">
                      Actualmente no se están recibiendo pagos por medios digitales.
                      Por favor, comunícate con administración para completar tu matriculación.
                    </p>
                  </div>
                )}
              </div>

              {/* Nota Azul */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex gap-2">
                  <span className="font-bold text-blue-800">Nota:</span>
                  <span className="text-blue-700">Asegúrate de guardar el comprobante de pago. Lo necesitarás en el siguiente paso.</span>
                </div>
              </div>

              {/* Importante Rojo */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-5">
                <h4 className="text-red-700 font-bold mb-3 uppercase">Importante:</h4>
                <ul className="list-disc list-inside text-xs text-red-700 space-y-2 leading-relaxed">
                  <li>En el Depósito Bancario debe figurar obligatoriamente: nombre, apellidos y número de cédula de identidad de la o el postulante.</li>
                  <li>Se requiere que el postulante o tutor en caso de menores de edad, se sirva verificar de forma detallada el cumplimiento de la totalidad de los requisitos establecidos previo a la realización del depósito bancario.</li>
                  <li>Esta diligencia es esencial a fin de evitar cualquier observación posterior que pudiera incidir en el proceso.</li>
                  <li>Se hace constar que la EBID no se responsabiliza por errores u omisiones del depositante. En este sentido, no se contempla la devolución por los depósitos realizados.</li>
                </ul>
              </div>
            </div>

            {/* 4. Documentación */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4 text-blue-900">4. Documentación</h2>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Fotografía Legalizada Título Bachiller*</label>
                  {existingDocs.titulo_bachiller && !files.titulo_bachiller ? (
                    <div className="border border-green-300 bg-green-50 rounded-md p-2">
                      <div className="flex items-center gap-2 text-green-700 text-sm mb-1">
                        <Check size={14} /> Documento existente
                      </div>
                      <label htmlFor="file_titulo" className="text-xs text-blue-600 cursor-pointer hover:underline">Cambiar archivo</label>
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleFileChange('titulo_bachiller', e)} className="hidden" id="file_titulo" />
                    </div>
                  ) : (
                    <div className="border rounded-md p-2 flex items-center gap-2">
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleFileChange('titulo_bachiller', e)} className="hidden" id="file_titulo" />
                      <label htmlFor="file_titulo" className="flex-1 text-sm text-gray-500 cursor-pointer truncate">
                        {files.titulo_bachiller ? files.titulo_bachiller.name : 'Seleccionar...'}
                      </label>
                      <Upload size={16} className="text-gray-400" />
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Fotografía Cédula de Identidad*</label>
                  {existingDocs.cedula_identidad && !files.cedula_identidad ? (
                    <div className="border border-green-300 bg-green-50 rounded-md p-2">
                      <div className="flex items-center gap-2 text-green-700 text-sm mb-1">
                        <Check size={14} /> Documento existente
                      </div>
                      <label htmlFor="file_ci" className="text-xs text-blue-600 cursor-pointer hover:underline">Cambiar archivo</label>
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleFileChange('cedula_identidad', e)} className="hidden" id="file_ci" />
                    </div>
                  ) : (
                    <div className="border rounded-md p-2 flex items-center gap-2">
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleFileChange('cedula_identidad', e)} className="hidden" id="file_ci" />
                      <label htmlFor="file_ci" className="flex-1 text-sm text-gray-500 cursor-pointer truncate">
                        {files.cedula_identidad ? files.cedula_identidad.name : 'Seleccionar...'}
                      </label>
                      <Upload size={16} className="text-gray-400" />
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Fotografía Comprobante Depósito Bancario*</label>
                  {existingDocs.comprobante_deposito && !files.comprobante_deposito ? (
                    <div className="border border-green-300 bg-green-50 rounded-md p-2">
                      <div className="flex items-center gap-2 text-green-700 text-sm mb-1">
                        <Check size={14} /> Documento existente
                      </div>
                      <label htmlFor="file_deposito" className="text-xs text-blue-600 cursor-pointer hover:underline">Cambiar archivo</label>
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleFileChange('comprobante_deposito', e)} className="hidden" id="file_deposito" />
                    </div>
                  ) : (
                    <div className="border rounded-md p-2 flex items-center gap-2">
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleFileChange('comprobante_deposito', e)} className="hidden" id="file_deposito" />
                      <label htmlFor="file_deposito" className="flex-1 text-sm text-gray-500 cursor-pointer truncate">
                        {files.comprobante_deposito ? files.comprobante_deposito.name : 'Seleccionar...'}
                      </label>
                      <Upload size={16} className="text-gray-400" />
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Fotografía Comprobante Depósito Bancario (trámites académicos y otros)</label>
                  <div className="border rounded-md p-2 flex items-center gap-2">
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleFileChange('comprobante_tramites', e)} className="hidden" id="file_tramites" />
                    <label htmlFor="file_tramites" className="flex-1 text-sm text-gray-500 cursor-pointer truncate">
                      {files.comprobante_tramites ? files.comprobante_tramites.name : 'Seleccionar...'}
                    </label>
                    <Upload size={16} className="text-gray-400" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Compromiso*</label>
                  <div className="border rounded-md p-2 flex items-center gap-2">
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleFileChange('compromiso', e)} className="hidden" id="file_compromiso" />
                    <label htmlFor="file_compromiso" className="flex-1 text-sm text-gray-500 cursor-pointer truncate">
                      {files.compromiso ? files.compromiso.name : 'Seleccionar...'}
                    </label>
                    <Upload size={16} className="text-gray-400" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Fotografía Constancia Seguro Médico*</label>
                  <div className="border rounded-md p-2 flex items-center gap-2">
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleFileChange('seguro_medico', e)} className="hidden" id="file_seguro" />
                    <label htmlFor="file_seguro" className="flex-1 text-sm text-gray-500 cursor-pointer truncate">
                      {files.seguro_medico ? files.seguro_medico.name : 'Seleccionar...'}
                    </label>
                    <Upload size={16} className="text-gray-400" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Fotografía Comprobante Multa Retraso Matriculación</label>
                  <div className="border rounded-md p-2 flex items-center gap-2">
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleFileChange('multa_retraso', e)} className="hidden" id="file_multa" />
                    <label htmlFor="file_multa" className="flex-1 text-sm text-gray-500 cursor-pointer truncate">
                      {files.multa_retraso ? files.multa_retraso.name : 'Seleccionar...'}
                    </label>
                    <Upload size={16} className="text-gray-400" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Fotografía 4x4 Tamaño Carnet</label>
                  {existingDocs.foto_carnet && !files.foto_carnet ? (
                    <div className="border border-green-300 bg-green-50 rounded-md p-2">
                      <div className="flex items-center gap-2 text-green-700 text-sm mb-1">
                        <Check size={14} /> Documento existente
                      </div>
                      <label htmlFor="file_foto" className="text-xs text-blue-600 cursor-pointer hover:underline">Cambiar archivo</label>
                      <input type="file" accept=".jpg,.jpeg,.png" onChange={(e) => handleFileChange('foto_carnet', e)} className="hidden" id="file_foto" />
                    </div>
                  ) : (
                    <div className="border rounded-md p-2 flex items-center gap-2">
                      <input type="file" accept=".jpg,.jpeg,.png" onChange={(e) => handleFileChange('foto_carnet', e)} className="hidden" id="file_foto" />
                      <label htmlFor="file_foto" className="flex-1 text-sm text-gray-500 cursor-pointer truncate">
                        {files.foto_carnet ? files.foto_carnet.name : 'Seleccionar...'}
                      </label>
                      <Upload size={16} className="text-gray-400" />
                    </div>
                  )}
                </div>
              </div>

              <p className="text-xs text-gray-500 mt-4">
                *La documentación adjuntada debe ser presentada también físicamente al área de Kardex. Adicionalmente, debe presentarse certificado de nacimiento original y 4 fotos tamaño carnet fondo blanco.
              </p>
            </div>

            {/* 5. Declaración Jurada */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4 text-blue-900">5. Declaración Jurada</h2>
              <div className="bg-gray-50 rounded p-4 text-sm mb-4 text-justify">
                <p>
                  Yo <strong>{datosPersonales.nombres} {datosPersonales.apellido_paterno} {datosPersonales.apellido_materno}</strong> por derecho, con Cédula de Identidad N.° <strong>{datosPersonales.cedula_identidad}</strong> de manera voluntaria acepto los términos establecidos en la Convocatoria y sus procesos de admisión y registro, así mismo declaro en honor a la verdad, que cumpliré a cabalidad con los requisitos indispensables exigidos en la convocatoria, siendo que todos los datos proporcionados en el presente formulario son verídicos, por lo que autorizo que la información proporcionada sea verificada y contrastada por la EBID, comprometiéndome a la presentación de la documentación el lugar de acuerdo al cronograma establecido. Asimismo, asumo absoluta responsabilidad por cualquier situación relacionada con la salud u accidentes que pudiera ocurrir durante el desarrollo de mis actividades académicas y extracurriculares en la EBID. En este sentido, autorizo/no autorizo a la EBID que, si fuera necesario, contacte a un familiar cercano así como al contacto de emergencia cuando sea necesario. En caso de no estar de acuerdo con lo estipulado, eximimos a la institución de toda responsabilidad respecto de accidentes relacionados a mi persona y de salud dentro de la institución.
                </p>
              </div>
              <p className="text-xs text-red-600 mb-4">
                Nota.- Se solicita verificar detalladamente la información registrada y el monto a cancelar previo a confirmar la inscripción, en virtud de que no se efectuarán devoluciones.
              </p>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={declaracionAceptada}
                  onChange={(e) => setDeclaracionAceptada(e.target.checked)}
                  className="w-5 h-5 rounded" />
                <span className="font-medium">Acepto la declaración jurada</span>
              </label>
            </div>

            {/* Botones de navegación */}
            <div className="flex justify-between">
              <button type="button" onClick={() => setStep(1)}
                className="px-8 py-3 bg-blue-900 text-white rounded-full font-semibold hover:bg-blue-800 transition flex items-center gap-2">
                <ArrowLeft size={18} /> Atrás
              </button>
              <button type="submit" disabled={!declaracionAceptada || submitting}
                className={`px-8 py-3 rounded-full font-semibold text-white transition flex items-center gap-2 ${declaracionAceptada && !submitting ? 'bg-blue-900 hover:bg-blue-800' : 'bg-gray-400 cursor-not-allowed'}`}>
                {submitting && <Loader className="animate-spin" size={18} />}
                {submitting ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default InscripcionesEstudiante;